import React, { useState, useEffect } from 'react';
import { FaTimes, FaPlus, FaEdit, FaTrash, FaInfoCircle, FaCheck } from 'react-icons/fa';
import formulaEvaluator from '../../../utils/formulaEvaluator';
import api from '../../../services/api';
import ThemedSelect from '../../common/ThemedSelect';

const CalculatedColumnModal = ({
  isOpen,
  onClose,
  onSave,
  existingColumns,
  availableColumns,
  currentGroupingId,
}) => {
  const [columns, setColumns] = useState([]);
  const [editingColumn, setEditingColumn] = useState(null);
  const [showFunctionHelp, setShowFunctionHelp] = useState(false);
  const [availableRoutes, setAvailableRoutes] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Load ALL columns from global storage, not just current route's columns
      const savedColumns = localStorage.getItem('calculatedColumns_global');
      if (savedColumns) {
        try {
          setColumns(JSON.parse(savedColumns));
        } catch (e) {
          console.error('Error loading all calculated columns:', e);
          setColumns(existingColumns || []);
        }
      } else {
        setColumns(existingColumns || []);
      }
      setEditingColumn(null);
      fetchAvailableRoutes();
    }
  }, [isOpen, existingColumns]);

  const fetchAvailableRoutes = async () => {
    setLoadingRoutes(true);
    try {
      // Call without date parameters to get all groupings
      const response = await api.routeSummary.getGroupings(null, null);
      console.log('Fetched routes response:', response.data);

      // The API returns an array directly, not wrapped in a groupings property
      const routes = Array.isArray(response.data) ? response.data : [];
      console.log('Available routes:', routes);
      setAvailableRoutes(routes);
    } catch (error) {
      console.error('Error fetching routes:', error);
      console.error('Error details:', error.response?.data || error.message);
      setAvailableRoutes([]);
    } finally {
      setLoadingRoutes(false);
    }
  };

  const handleAddColumn = () => {
    setEditingColumn({
      id: Date.now(),
      key: '',
      label: '',
      formula: '',
      type: 'number',
      decimals: 2,
      isNew: true,
      appliedRoutes: [currentGroupingId], // Default to current route
    });
  };

  const handleEditColumn = (column) => {
    // Ensure appliedRoutes exists for backward compatibility
    setEditingColumn({
      ...column,
      appliedRoutes: column.appliedRoutes || [currentGroupingId],
    });
  };

  const handleDeleteColumn = (columnId) => {
    if (window.confirm('Are you sure you want to delete this calculated column?')) {
      setColumns(columns.filter((col) => col.id !== columnId));
    }
  };

  const handleSaveColumn = () => {
    // Validate
    if (!editingColumn.key || !editingColumn.label || !editingColumn.formula) {
      alert('Please fill in all required fields');
      return;
    }

    // Validate that at least one route is selected
    if (!editingColumn.appliedRoutes || editingColumn.appliedRoutes.length === 0) {
      alert('Please select at least one route for this calculated column');
      return;
    }

    // Validate key format (alphanumeric and underscore only)
    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(editingColumn.key)) {
      alert(
        'Column key must start with a letter or underscore and contain only letters, numbers, and underscores'
      );
      return;
    }

    // Check for duplicate keys
    const isDuplicate = columns.some(
      (col) => col.key === editingColumn.key && col.id !== editingColumn.id
    );
    if (isDuplicate) {
      alert('A column with this key already exists');
      return;
    }

    // Validate formula
    const columnKeys = availableColumns.map((col) => col.key);
    const validation = formulaEvaluator.validate(editingColumn.formula, columnKeys);
    if (!validation.valid) {
      alert(`Formula error: ${validation.error}`);
      return;
    }

    // Save column
    if (editingColumn.isNew) {
      setColumns([...columns, { ...editingColumn, isNew: false }]);
    } else {
      setColumns(columns.map((col) => (col.id === editingColumn.id ? editingColumn : col)));
    }
    setEditingColumn(null);
  };

  const handleCancelEdit = () => {
    setEditingColumn(null);
  };

  const handleSaveAll = () => {
    onSave(columns);
    onClose();
  };

  const handleRouteToggle = (routeId) => {
    setEditingColumn((prev) => {
      const currentRoutes = prev.appliedRoutes || [];
      const isSelected = currentRoutes.includes(routeId);

      return {
        ...prev,
        appliedRoutes: isSelected
          ? currentRoutes.filter((id) => id !== routeId)
          : [...currentRoutes, routeId],
      };
    });
  };

  const getRouteNames = (routeIds) => {
    if (!routeIds || routeIds.length === 0) return 'No routes';
    if (routeIds.length === availableRoutes.length) return 'All routes';

    const names = routeIds
      .map((id) => availableRoutes.find((r) => r.id === id)?.name)
      .filter(Boolean);

    if (names.length === 0) return 'Unknown routes';
    if (names.length <= 2) return names.join(', ');
    return `${names.length} routes`;
  };

  const availableFunctions = formulaEvaluator.getAvailableFunctions();

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white flex justify-between items-center">
          <h2 className="text-xl font-bold">Manage Calculated Columns</h2>
          <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Info Banner */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start">
            <FaInfoCircle className="text-blue-600 mt-1 mr-3 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">Create custom calculated columns using formulas</p>
              <p>
                Reference existing columns using [columnKey] syntax. Example:{' '}
                <code className="bg-blue-100 px-1 rounded">[nettWeight] * 1.1</code>
              </p>
              <button
                onClick={() => setShowFunctionHelp(!showFunctionHelp)}
                className="mt-2 text-blue-600 hover:text-blue-800 underline text-xs"
              >
                {showFunctionHelp ? 'Hide' : 'Show'} available functions
              </button>
            </div>
          </div>

          {/* Function Help */}
          {showFunctionHelp && (
            <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <h3 className="font-semibold mb-3 text-gray-700">Available Functions:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {availableFunctions.map((func) => (
                  <div key={func.name} className="text-sm">
                    <code className="bg-gray-200 px-2 py-1 rounded font-mono text-xs">
                      {func.name}
                    </code>
                    <p className="text-gray-600 mt-1">{func.description}</p>
                    <p className="text-gray-500 text-xs mt-1">
                      Example: <code className="bg-gray-100 px-1">{func.example}</code>
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-300">
                <h4 className="font-semibold text-sm text-gray-700 mb-2">Available Columns:</h4>
                <div className="flex flex-wrap gap-2">
                  {availableColumns.map((col) => (
                    <code
                      key={col.key}
                      className="bg-primary-100 text-primary-800 px-2 py-1 rounded text-xs"
                    >
                      [{col.key}]
                    </code>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Existing Columns List */}
          {!editingColumn && (
            <div className="space-y-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-700">
                  Calculated Columns ({columns.length})
                </h3>
                <button
                  onClick={handleAddColumn}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <FaPlus className="mr-2" />
                  Add Column
                </button>
              </div>

              {columns.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p className="mb-4">No calculated columns yet</p>
                  <button
                    onClick={handleAddColumn}
                    className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Create Your First Column
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {columns.map((column) => (
                    <div
                      key={column.id}
                      className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 transition-colors bg-surface"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <h4 className="font-semibold text-gray-800">{column.label}</h4>
                            <code className="ml-3 text-xs bg-gray-100 px-2 py-1 rounded text-gray-600">
                              {column.key}
                            </code>
                            <span className="ml-2 text-xs bg-primary-100 text-primary-800 px-2 py-1 rounded">
                              {column.type}
                            </span>
                            <span className="ml-2 text-xs bg-secondary-100 text-secondary-800 px-2 py-1 rounded">
                              {getRouteNames(column.appliedRoutes)}
                            </span>
                          </div>
                          <code className="text-sm bg-gray-50 px-3 py-2 rounded block text-gray-700 font-mono mb-2">
                            {column.formula}
                          </code>
                          {column.appliedRoutes &&
                            column.appliedRoutes.length > 0 &&
                            column.appliedRoutes.length < availableRoutes.length && (
                              <div className="text-xs text-gray-500">
                                Applied to:{' '}
                                {column.appliedRoutes
                                  .map(
                                    (id) =>
                                      availableRoutes.find((r) => r.id === id)?.name ||
                                      `Route ${id}`
                                  )
                                  .join(', ')}
                              </div>
                            )}
                        </div>
                        <div className="flex space-x-2 ml-4">
                          <button
                            onClick={() => handleEditColumn(column)}
                            className="p-2 text-primary-600 hover:bg-primary-50 rounded transition-colors"
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleDeleteColumn(column.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Edit/Add Column Form */}
          {editingColumn && (
            <div className="bg-gray-50 p-6 rounded-lg border-2 border-primary-300">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">
                {editingColumn.isNew ? 'Add New Column' : 'Edit Column'}
              </h3>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Column Label <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={editingColumn.label}
                      onChange={(e) =>
                        setEditingColumn({ ...editingColumn, label: e.target.value })
                      }
                      placeholder="e.g., Adjusted Weight"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Column Key <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={editingColumn.key}
                      onChange={(e) => setEditingColumn({ ...editingColumn, key: e.target.value })}
                      placeholder="e.g., adjustedWeight"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">Alphanumeric and underscore only</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Formula <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={editingColumn.formula}
                    onChange={(e) =>
                      setEditingColumn({ ...editingColumn, formula: e.target.value })
                    }
                    placeholder="e.g., [nettWeight] * 1.1 or IF([ticketType] = 'Dispatch', [nettWeight], 0)"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use [columnKey] to reference columns. Click "Show available functions" above for
                    help.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data Type
                    </label>
                    <ThemedSelect
                      value={editingColumn.type}
                      onChange={(e) => setEditingColumn({ ...editingColumn, type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="number">Number</option>
                      <option value="text">Text</option>
                      <option value="boolean">Boolean</option>
                    </ThemedSelect>
                  </div>

                  {editingColumn.type === 'number' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Decimal Places
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        value={editingColumn.decimals}
                        onChange={(e) =>
                          setEditingColumn({
                            ...editingColumn,
                            decimals: parseInt(e.target.value) || 0,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  )}
                </div>

                {/* Route Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Apply to Routes <span className="text-red-500">*</span>
                  </label>
                  {loadingRoutes ? (
                    <div className="text-sm text-gray-500">Loading routes...</div>
                  ) : (
                    <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto bg-gray-50">
                      {availableRoutes.length === 0 ? (
                        <div className="text-sm text-gray-500">No routes available</div>
                      ) : (
                        <div className="space-y-2">
                          {availableRoutes.map((route) => (
                            <label
                              key={route.id}
                              className="flex items-center p-2 hover:bg-primary-50 rounded cursor-pointer transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={(editingColumn.appliedRoutes || []).includes(route.id)}
                                onChange={() => handleRouteToggle(route.id)}
                                className="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                              />
                              <span className="text-sm text-gray-700">{route.name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Select which routes should display this calculated column
                  </p>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-300">
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveColumn}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <FaCheck className="mr-2" />
                    Save Column
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!editingColumn && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAll}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Save & Apply
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalculatedColumnModal;
